import * as React from 'react';
import {FunctionComponent, useState} from 'react';
import {ReportData} from '../CompareReportPresenter';
import {classNames} from '../../../../Support/classNames';
import {calculateSplitTimes, Split, SplitStrategy, Strategy} from '../../../../../Business/calculateSplitTimes';
import {Distance} from '../../../../../Domain/Distance';
import {Fraction} from '../../../../../Domain/Fraction';
import {InfoTooltip} from '../../../../Components/Utils/InfoTooltip';
import {formatDistance} from '../../../../Support/Formatting/formatDistance';
import {formatSplitStrategy} from '../../../../Components/Utils/FormatSplitStrategy';
import {tableColumnTitles} from '../Utils/TableColumnTitles';
import {strokesForSnippet} from '../Utils/strokesForSnippet';
import {formatAverageSplit} from '../../../../Support/Formatting/formatSplit';
import {toJS} from 'mobx';
import {withOrdinal} from '../../../../Support/withOrdinal';
import {ReportField} from '../../Models/ReportField';
import {formatAveragePower} from '../../../../Support/Formatting/formatPower';
import {formatAverageStrokeRate} from '../../../../Support/Formatting/formatStrokeRate';

interface OwnProps {
    splitStrategy: SplitStrategy;
    reportData: ReportData;
    className?: string;
}

enum Statistic {
    Split = 'split',
    Power = 'power',
    StrokeRate = 'stroke_rate',
}

function statisticLabel(statistic: Statistic) {
    switch (statistic) {
        case Statistic.Split:
            return 'Split (/500m)';
        case Statistic.Power:
            return 'Power (W)';
        case Statistic.StrokeRate:
            return 'Stroke rate (/min)';
    }
}

function selectFormattedStatisticValue(statistic: Statistic, split: Split): string {
    switch (statistic) {
        case Statistic.Split:
            return formatAverageSplit(split.speed).formatted;
        case Statistic.Power:
            return split.power ? formatAveragePower(split.power).formatted : '-';
        case Statistic.StrokeRate:
            return split.strokeRate ? formatAverageStrokeRate(split.strokeRate).formatted : '-';
    }
}

export const SplitTable: FunctionComponent<OwnProps> = ({splitStrategy, reportData, className}) => {
    const [activeStatistic, setActiveStatistic] = useState(Statistic.Split);
    const snippets = [...reportData.snippets].sort((a, b) => {
        return a.publicWorkout.executedAt.getTime() - b.publicWorkout.executedAt.getTime();
    });

    const snippetsWithSplitData = snippets.map((snippet) => {
        const downloadedIndoorWorkout = reportData.downloadedIndoorWorkouts.find((dIw) => {
            return dIw.id === snippet.publicWorkout.id;
        });
        return {
            snippet: snippet,
            splits: downloadedIndoorWorkout
                ? calculateSplitTimes(strokesForSnippet(snippet, downloadedIndoorWorkout), splitStrategy)
                : null,
        };
    });

    const numberOfRows = Math.max(...snippetsWithSplitData.map((snippet) => snippet.splits?.length || 0));

    return (
        <div className={classNames('row', className)}>
            <div className="col-12">
                <div className="card progresstable">
                    <div className="card-title">
                        <h5 className="d-flex flex-row justify-content-between">
                            <span>
                                Segments
                                <InfoTooltip
                                    className="ms-2"
                                    description="Based on interpolated strokes, can differ from interval results"
                                />
                            </span>
                            <div className="dropdown">
                                <button
                                    className="btn btn-secondary btn-sm dropdown-toggle"
                                    type="button"
                                    id="splitcardmode"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    {statisticLabel(activeStatistic)}
                                </button>
                                <ul className="dropdown-menu" aria-labelledby="splitcardmode">
                                    {Object.values(Statistic).map((statistic) => {
                                        return (
                                            <li key={statistic}>
                                                <button
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        setActiveStatistic(statistic);
                                                    }}
                                                >
                                                    {statisticLabel(statistic)}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </h5>
                    </div>
                    <table className="table">
                        <thead>
                            <tr>
                                <th />
                                {tableColumnTitles(snippets).map((title) => (
                                    <th>{title}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({length: numberOfRows}).map((_, index) => {
                                return (
                                    <tr>
                                        <td>{withOrdinal(index + 1)}</td>
                                        {snippetsWithSplitData.map((snippet) => {
                                            const split = snippet.splits?.[index];
                                            if (!split) {
                                                if (index === 0) {
                                                    return <td className="progresstable__muted">Unavailable</td>;
                                                }
                                                return <td></td>;
                                            }
                                            return <td>{selectFormattedStatisticValue(activeStatistic, split)}</td>;
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
